import {
  Box,
  Button,
  Form,
  HStack,
  MultiSelectField,
  SubmitButton,
  useToast,
} from '@medely/ui-kit';
import React from 'react';
import { ContentLoader } from '../ContentLoader';
import { ehrSchema } from './schema';
import { useAssignmentRequestsForEHRs, useEHRExperiences, useEHRs } from './graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useEhrMutations } from './mutations';

type ElectronicHealthRecordsContainerProps = {
  onSuccessRedirect: () => void;
  assignmentId?: number;
  isModal?: boolean;
};

export const ElectronicHealthRecordsContainer = ({
  onSuccessRedirect,
  assignmentId,
  isModal = false,
}: ElectronicHealthRecordsContainerProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { addEHR, isLoading: isMutationLoading } = useEhrMutations();
  const { ehrs, isLoading: isEhrsLoading } = useEHRs();
  const { userEhrs, isLoading: isUserEhrsLoading } = useEHRExperiences();
  const { assignmentRequests } = useAssignmentRequestsForEHRs({ assignmentId });
  const ehrOptions = ehrs.map(({ id, label }) => ({ value: id, label: label || '' }));

  const initialValues = { ehr_systems: userEhrs.map(({ id }) => id) };
  const isLoading = isEhrsLoading || isUserEhrsLoading;

  const handleSubmit = (values: number[]) => {
    addEHR(
      {
        values,
        assignment_request_id: !!assignmentRequests?.length ? assignmentRequests[0]?.id : undefined,
      },
      {
        onSuccess: () => {
          onSuccessRedirect();
          queryClient.invalidateQueries(['ehrs']);
          toast.success('Electronic Health Record added');
        },
      },
    );
  };

  return (
    <ContentLoader loading={isLoading}>
      <>
        <Form handleSubmit={handleSubmit} schema={ehrSchema} defaultValues={initialValues}>
          <Box px={2} testId="ehr_systems">
            <MultiSelectField
              name="ehr_systems"
              label="Select EHRs *"
              options={ehrOptions}
              variant={isModal ? 'default' : 'list'}
            />
          </Box>
          <HStack gap={isModal ? 2 : 0} justifyContent="space-around" pt={2}>
            {isModal && (
              <Button
                onClick={() => onSuccessRedirect()}
                variant="outlined"
                disabled={isMutationLoading}
                fullWidth
              >
                Back
              </Button>
            )}
            <SubmitButton
              loading={isMutationLoading}
              disabled={isMutationLoading}
              testId="ehr_systems-save-button"
              fullWidth
            >
              Save
            </SubmitButton>
          </HStack>
        </Form>
      </>
    </ContentLoader>
  );
};
